import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/api';
import { Ticket, QrCode, RotateCw, Link as CopyIcon, AlertTriangle, Download } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';

const CodeRedemption = () => {
  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [storeData, setStoreData] = useState(null);

  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const storeId = userData?.storeId;

  const loadStoreInfo = useCallback(async () => {
    if (!storeId || storeId === '0') return;

    setLoading(true);
    try {
      const res = await api.get(`/api/stores/detail/${storeId}`);
      if (res.data.success) {
        setStoreData(res.data.store);
      }
    } catch (err) {
      console.error('Fetch Error:', err);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    loadStoreInfo();
  }, [loadStoreInfo]);

  const downloadQRCode = () => {
    const canvas = document.querySelector('canvas');
    if (!canvas) return;

    const pngUrl = canvas
      .toDataURL('image/png')
      .replace('image/png', 'image/octet-stream');

    const downloadLink = document.createElement('a');
    downloadLink.href = pngUrl;
    downloadLink.download = `${storeData?.storeName || 'Store'}_QR.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const handleRedeem = async () => {
    const trimmedCode = code.trim();

    if (!trimmedCode) {
      setMessage('Please enter a code');
      setMessageType('error');
      return;
    }

    setRedeeming(true);

    try {
      const res = await api.post('/api/coupons/redeem-auto', {
        code: trimmedCode,
      });

      if (res.data.success) {
        setMessage(`✅ ${res.data.message} — Won: ${res.data.wonValue}`);
        setMessageType('success');
        setCode('');
        loadStoreInfo();
      } else {
        setMessage(`❌ ${res.data.message || 'Invalid'}`);
        setMessageType('error');
      }
    } catch (err) {
      setMessage(`❌ ${err.response?.data?.message || 'Invalid'}`);
      setMessageType('error');
    } finally {
      setRedeeming(false);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const finalSpinUrl = storeData?.spinUrl || 'Loading URL...';
  const isExhausted = storeData?.isExhausted;
  const spinLimit = storeData?.spinLimit;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-6 text-slate-800 flex items-center gap-2">
          <Ticket className="text-green-600" /> Redemption Panel
        </h1>

        {storeId && (
          <div className="bg-white rounded-xl shadow-md p-5 mb-6 border-l-4 border-blue-500">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-500">Store Name</p>
                <p className="font-bold text-xl">{storeData?.storeName || '...'}</p>
                <p className="text-xs text-gray-400 mt-1">{spinLimit ?? '...'}</p>
              </div>

              <div className="text-right">
                <p className="text-sm text-gray-500">Spin Limit</p>
                <div className="flex items-center gap-2 justify-end">
                  <span
                    className={`text-2xl font-bold ${
                      isExhausted ? 'text-red-500' : 'text-blue-600'
                    }`}
                  >
                    {spinLimit ?? '...'}
                  </span>
                  <button
                    onClick={loadStoreInfo}
                    disabled={loading}
                    className="p-1 hover:bg-gray-100 rounded-full"
                  >
                    <RotateCw size={18} className={loading ? 'animate-spin' : ''} />
                  </button>
                </div>

                {storeData && (
                  <span
                    className={`text-xs font-semibold mt-1 inline-block px-2 py-0.5 rounded-full ${
                      isExhausted
                        ? 'bg-red-100 text-red-600'
                        : 'bg-green-100 text-green-600'
                    }`}
                  >
                    {isExhausted ? '⛔ Exhausted' : '● Active'}
                  </span>
                )}
              </div>
            </div>

            {isExhausted && (
              <div className="mt-4 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700">
                <AlertTriangle size={18} />
                <p className="text-sm font-semibold">
                  Spin limit exhausted for this store. Please contact admin to add more spins.
                </p>
              </div>
            )}

            <div className="mt-6 flex flex-col items-center border-t pt-6">
              <p className="text-sm font-semibold mb-4 text-gray-600 flex items-center gap-2">
                <QrCode size={18} /> SCAN TO SPIN
              </p>

              <div className="bg-white p-4 rounded-xl border-2 border-gray-100 shadow-sm relative group">
                {storeData?.spinUrl ? (
                  <>
                    <QRCodeCanvas value={finalSpinUrl} size={200} level="H" />
                    <button
                      onClick={downloadQRCode}
                      className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-xl text-white font-bold gap-2"
                    >
                      <Download size={24} /> Download
                    </button>
                  </>
                ) : (
                  <div className="w-[200px] h-[200px] bg-gray-50 flex items-center justify-center text-gray-400">
                    Loading...
                  </div>
                )}
              </div>

              <button
                onClick={downloadQRCode}
                className="mt-3 flex items-center gap-2 text-sm text-blue-600 font-semibold hover:underline"
              >
                <Download size={16} /> Save QR Code
              </button>

              <div className="mt-4 flex items-center gap-2 bg-gray-100 p-2 px-4 rounded-full w-full max-w-lg">
                <code className="text-[10px] flex-1 truncate text-gray-500">
                  {finalSpinUrl}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(finalSpinUrl);
                    alert('URL Copied!');
                  }}
                >
                  <CopyIcon size={14} className="text-blue-500" />
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold mb-4">Redeem Winning Coupon</h2>

          <div className="flex gap-3">
            <input
              className="flex-1 border-2 p-3 rounded-lg focus:border-green-500 outline-none"
              placeholder="Enter code here..."
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleRedeem()}
            />
            <button
              onClick={handleRedeem}
              disabled={redeeming}
              className="bg-green-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-green-700 disabled:bg-gray-400 transition-colors"
            >
              {redeeming ? 'Checking...' : 'REDEEM'}
            </button>
          </div>

          {message && (
            <p
              className={`mt-4 p-3 rounded-lg text-center font-bold ${
                messageType === 'success'
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-700'
              }`}
            >
              {message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CodeRedemption;